'use client';

import { User } from '@api/models';
import { useTranslations } from 'next-intl';
import React, { useCallback, useState } from 'react';

import { ActivityDrawer } from '@website/components/ActivityDrawer/ActivityDrawer';
import { GoogleMapWrapper } from '@website/components/GoogleMapWrapper';
import { ListingCardsScreenWrapper } from '@website/components/ListingCardsScreenWrapper/ListingCardsScreenWrapper';
import { useActivities } from '@website/hooks/useActivities';
import { useActivityCardRefs } from '@website/hooks/useActivityCardRefs';
import { useActivitySelection } from '@website/hooks/useActivitySelection';
import { useFilteredActivities } from '@website/hooks/useFilteredActivities';
import { useSearchApiParams } from '@website/hooks/useSearchApiParams';
import { type MapBounds } from '@website/utils/mapUtils';

interface ActivitiesListWrapperProps {
  isMapDisplayMode: boolean;
  selectedActivityId: string | undefined;
  setSelectedActivityId: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectionSource: 'map' | 'list' | null;
  setSelectionSource: React.Dispatch<React.SetStateAction<'map' | 'list' | null>>;
  user: User | null;
  mapBounds: MapBounds | null;
  handleMapIdle: (
    bounds: { neLat: number; neLng: number; swLat: number; swLng: number },
    zoom: number | undefined
  ) => void;
  center: { lat: number; lng: number } | null;
  zoom: number;
  panVersion?: number;
}

export default function ActivitiesListWrapper({
  isMapDisplayMode,
  selectedActivityId,
  setSelectedActivityId,
  selectionSource,
  setSelectionSource,
  user,
  mapBounds,
  handleMapIdle,
  center,
  zoom,
  panVersion
}: ActivitiesListWrapperProps) {
  const t = useTranslations('ListingCardsScreen');
  const apiParams = useSearchApiParams(mapBounds);
  const isLocationResolving = !mapBounds;

  const {
    data: activitiesResult,
    isLoading: isListActivitiesLoading,
    isFetching: isListActivitiesFetching,
    isError: isListActivitiesError
  } = useActivities(apiParams, {
    enabled: !isLocationResolving
  });

  const listActivities = activitiesResult?.activities;
  const fallbackActivities = activitiesResult?.fallbackActivities;

  const {
    hoveredActivityId,
    activityForDrawerData,
    handleLocationSelect,
    handleDrawerClose,
    handleActivityHover: baseHandleActivityHover,
    handleActivityLeaveHover: baseHandleActivityLeaveHover
  } = useActivitySelection({
    activities: listActivities,
    selectedActivityId,
    setSelectedActivityId,
    selectionSource,
    setSelectionSource
  });

  const { listContainerRef, registerCardRef, scrollCardIntoView, isMobile } = useActivityCardRefs();

  const handleActivityHover = useCallback(
    (activityId: string) => {
      baseHandleActivityHover(activityId);
    },
    [baseHandleActivityHover]
  );

  const handleActivityLeaveHover = useCallback(() => {
    baseHandleActivityLeaveHover();
  }, [baseHandleActivityLeaveHover]);

  const { activitiesForListingCards, mapLocations, atHomeActivities } = useFilteredActivities({
    activities: listActivities,
    zoom
  });

  const hasFewResults =
    !isLocationResolving &&
    !isListActivitiesLoading &&
    (!activitiesForListingCards || activitiesForListingCards.length < 3);

  // --- Group navigation for mobile drawer ---
  const [drawerGroupIds, setDrawerGroupIds] = useState<string[]>([]);
  const [drawerGroupIndex, setDrawerGroupIndex] = useState(0);

  const handleMapLocationSelect = useCallback(
    (id: string, source?: 'map' | 'list') => {
      handleLocationSelect(id, source);
      if (source === 'map') {
        scrollCardIntoView(id);

        const atHomeIds = atHomeActivities?.map((a) => a.id) ?? [];
        if (atHomeIds.includes(id)) {
          setDrawerGroupIds(atHomeIds);
          setDrawerGroupIndex(atHomeIds.indexOf(id));
          return;
        }
        for (const loc of mapLocations ?? []) {
          if (loc.activityIds && loc.activityIds.includes(id) && loc.activityIds.length > 1) {
            setDrawerGroupIds(loc.activityIds);
            setDrawerGroupIndex(loc.activityIds.indexOf(id));
            return;
          }
        }
        setDrawerGroupIds([]);
        setDrawerGroupIndex(0);
      }
    },
    [handleLocationSelect, scrollCardIntoView, atHomeActivities, mapLocations]
  );

  const handleDrawerPrev = useCallback(() => {
    if (drawerGroupIds.length === 0) return;
    const newIdx = (drawerGroupIndex - 1 + drawerGroupIds.length) % drawerGroupIds.length;
    setDrawerGroupIndex(newIdx);
    const newId = drawerGroupIds[newIdx]!;
    handleLocationSelect(newId, 'map');
    scrollCardIntoView(newId);
  }, [drawerGroupIds, drawerGroupIndex, handleLocationSelect, scrollCardIntoView]);

  const handleDrawerNext = useCallback(() => {
    if (drawerGroupIds.length === 0) return;
    const newIdx = (drawerGroupIndex + 1) % drawerGroupIds.length;
    setDrawerGroupIndex(newIdx);
    const newId = drawerGroupIds[newIdx]!;
    handleLocationSelect(newId, 'map');
    scrollCardIntoView(newId);
  }, [drawerGroupIds, drawerGroupIndex, handleLocationSelect, scrollCardIntoView]);

  return (
    <div
      className={`flex flex-row flex-1 h-full md:w-full layout-bounds ${isMobile && isMapDisplayMode ? 'mx-0' : ''}`}
    >
      {/* List view - hide on mobile when isMapDisplayMode is true */}
      <div
        className={`flex flex-col w-full md:w-1/2 md:h-[calc(100vh-120px)] ${isMapDisplayMode ? 'hidden md:flex' : ''}`}
      >
        {/* Activity count - fixed above scroll area */}
        {(activitiesForListingCards?.length ?? 0) > 0 && (
          <div className="flex-shrink-0 text-xs font-semibold text-[#1F242F] pt-5 pb-2 md:pl-[5px] md:pr-[14px]">
            {(activitiesForListingCards?.length ?? 0) === 1
              ? `${t('activitiesFoundSingularPrefix')}${activitiesForListingCards?.length ?? 0}${t('activitiesFoundSingularSuffix')}`
              : `${t('activitiesFoundPluralPrefix')}${activitiesForListingCards?.length ?? 0}${t('activitiesFoundPluralSuffix')}`}
          </div>
        )}

        {/* Scrollable list */}
        <div
          ref={listContainerRef}
          className="flex flex-col flex-1 min-h-0 pb-5 md:pl-[5px] md:pr-[14px] md:overflow-y-auto"
        >
          <ListingCardsScreenWrapper
            activities={activitiesForListingCards ?? []}
            totalCount={activitiesForListingCards?.length ?? 0}
            showActivityCount={false}
            isLoading={
              isLocationResolving ||
              ((isListActivitiesLoading || isListActivitiesFetching) && !activitiesForListingCards?.length)
            }
            isError={isListActivitiesError}
            onActivityHover={handleActivityHover}
            onActivityLeaveHover={handleActivityLeaveHover}
            hoveredActivityId={hoveredActivityId ?? undefined}
            selectedActivityId={selectedActivityId}
            registerCardRef={registerCardRef}
            onActivitySelect={(id) => {
              handleLocationSelect(id, 'list');
              scrollCardIntoView(id);
            }}
            user={user}
            topActivities={hasFewResults ? (fallbackActivities ?? []) : undefined}
            isTopActivitiesLoading={false}
          />
        </div>
      </div>

      {/* Map view */}
      <div
        className={`
          ${isMapDisplayMode ? 'flex-1 z-30' : 'max-md:absolute max-md:inset-0 max-md:opacity-0 max-md:pointer-events-none max-md:-z-10 md:block z-30'} 
          flex w-full md:w-1/2
        `}
      >
        <GoogleMapWrapper
          noLocation={false}
          defaultCenter={center ?? undefined}
          locations={mapLocations}
          atHomeActivities={atHomeActivities}
          centerLat={center?.lat ?? null}
          centerLng={center?.lng ?? null}
          zoom={zoom}
          panVersion={panVersion}
          className="w-full h-full"
          onLocationSelect={handleMapLocationSelect}
          onMarkerHover={handleActivityHover}
          onMarkerLeaveHover={handleActivityLeaveHover}
          selectedMarkerId={selectedActivityId}
          hoveredMarkerId={hoveredActivityId ?? undefined}
          onBoundsChanged={handleMapIdle}
          isLoading={isLocationResolving || isListActivitiesFetching}
        />
      </div>

      {/* Mobile activity drawer with group navigation */}
      {isMobile && activityForDrawerData && selectionSource === 'map' && (
        <ActivityDrawer
          activity={activityForDrawerData}
          isOpen={!!selectedActivityId}
          onClose={handleDrawerClose}
          onPrev={drawerGroupIds.length > 1 ? handleDrawerPrev : undefined}
          onNext={drawerGroupIds.length > 1 ? handleDrawerNext : undefined}
          currentIndex={drawerGroupIndex}
          totalCount={drawerGroupIds.length > 1 ? drawerGroupIds.length : undefined}
        />
      )}
    </div>
  );
}
